import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { 
  Search, Filter, LogOut, User, Mail, Phone, Calendar, 
  FileText, MapPin, Eye, Loader2, Users, Settings, Tag, TrendingUp, Images, CalendarDays, Menu, Download, Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import RequestDetailModal from '../../components/admin/RequestDetailModal';
import UserManagement from '../../components/admin/UserManagement';
import CategoryManagement from '../../components/admin/CategoryManagement';
import EventSettings from '../../components/admin/EventSettings';
import SlideshowManager from '../../components/admin/SlideshowManager';
import ScheduleManager from '../../components/admin/ScheduleManager';
import EmailTemplatesManager from '../../components/admin/EmailTemplatesManager';
import { getDisplayStatus, isNoShow } from '../../utils/statusDisplay';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_COLORS = {
  'New': 'bg-blue-100 text-blue-800 border-blue-300',
  'Assigned': 'bg-purple-100 text-purple-800 border-purple-300',
  'More Information Needed': 'bg-yellow-100 text-yellow-800 border-yellow-300',
  'In Progress': 'bg-indigo-100 text-indigo-800 border-indigo-300',
  'Completed': 'bg-green-100 text-green-800 border-green-300',
  'Collected/Returned': 'bg-teal-100 text-teal-800 border-teal-300',
  'Unrepairable': 'bg-orange-100 text-orange-800 border-orange-300',
  'Cancelled': 'bg-red-100 text-red-800 border-red-300',
  'No Show': 'bg-rose-100 text-rose-800 border-rose-300'
};

// Centralised tab definition — single source of truth for both the
// desktop tab strip and the mobile dropdown. Order matters for the UI.
const TAB_DEFS = [
  { id: 'requests', label: 'Repair Requests', icon: FileText, roles: ['admin', 'super_admin'] },
  { id: 'users', label: 'Users', icon: Users, roles: ['admin', 'super_admin'] },
  { id: 'categories', label: 'Categories', icon: Tag, roles: ['super_admin'] },
  { id: 'event-settings', label: 'Event Settings', icon: Settings, roles: ['super_admin'], testId: 'tab-event-settings' },
  { id: 'schedule', label: 'Schedule', icon: CalendarDays, roles: ['admin', 'super_admin'], testId: 'tab-schedule' },
  { id: 'slideshow', label: 'Slideshow', icon: Images, roles: ['super_admin'], testId: 'tab-slideshow' },
  { id: 'email-templates', label: 'Email Templates', icon: Mail, roles: ['super_admin'], testId: 'tab-email-templates' },
];

const visibleTabs = (userRole) =>
  TAB_DEFS.filter((t) => t.roles.includes(userRole));

// Compact <select>-based navigation for mobile (< md). One tap to switch
// sections, zero horizontal scroll. Native <select> is keyboard-friendly
// and a11y-friendly out of the box.
function MobileTabSelect({ currentView, onChange, userRole }) {
  const tabs = visibleTabs(userRole);
  const current = tabs.find((t) => t.id === currentView) || tabs[0];
  return (
    <div className="md:hidden py-2 flex items-center gap-2" data-testid="admin-tabs-mobile">
      <Menu className="w-4 h-4 text-gray-500 flex-shrink-0" aria-hidden="true" />
      <label htmlFor="admin-tab-select" className="sr-only">Dashboard section</label>
      <select
        id="admin-tab-select"
        data-testid="admin-tab-select"
        value={currentView}
        onChange={(e) => onChange(e.target.value)}
        className="flex-1 min-w-0 rounded-md border-2 border-[#193460] bg-white py-2 px-3 text-sm font-medium text-[#193460] focus:outline-none focus:ring-2 focus:ring-[#E87B6A]"
      >
        {tabs.map((t) => (
          <option key={t.id} value={t.id}>{t.label}</option>
        ))}
      </select>
      {current?.icon && <current.icon className="w-4 h-4 text-[#E87B6A] flex-shrink-0" aria-hidden="true" />}
    </div>
  );
}

// Desktop horizontal tab strip (>= md).
function DesktopTabs({ currentView, onChange, userRole }) {
  return (
    <>
      {visibleTabs(userRole).map((t) => {
        const isActive = currentView === t.id;
        const Icon = t.icon;
        return (
          <button
            key={t.id}
            onClick={() => onChange(t.id)}
            data-testid={t.testId || `tab-${t.id}`}
            className={`flex-shrink-0 px-4 lg:px-6 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              isActive
                ? 'border-[#E87B6A] text-[#E87B6A]'
                : 'border-transparent text-gray-600 hover:text-gray-800'
            }`}
          >
            <Icon className="w-4 h-4 inline mr-2" />
            {t.label}
          </button>
        );
      })}
    </>
  );
}

export default function AdminDashboard() {
  const { user, logout, getAuthHeader } = useAuth();
  const [requests, setRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [view, setView] = useState('requests'); // 'requests' or 'users' or 'categories'
  // Event-filter dropdown next to the Export Log button — lists the
  // display strings from event_schedule plus a top "All events" option.
  const [exportEventList, setExportEventList] = useState([]);
  const [exportEvent, setExportEvent] = useState('');
  // Events that the admin has successfully exported in THIS session.
  // The "Archive & Clear" action is gated on the selected event being
  // present here, so we can never delete bookings the operator hasn't
  // confirmed they have a local copy of.
  const [exportedThisSession, setExportedThisSession] = useState(() => new Set());
  // Map of event_display → sort_date (ISO yyyy-mm-dd), used to gate
  // archive-and-clear to past events only without a second API call.
  const [eventSortDates, setEventSortDates] = useState({});
  const [archiving, setArchiving] = useState(false);

  const statuses = [
    'New', 
    'Assigned', 
    'More Information Needed',
    'Completed',
    'Unrepairable',
    'Cancelled',
    'No Show',
  ];

  useEffect(() => {
    fetchRequests();
    fetchStats();
    fetchScheduleForExport();
  }, []);

  const fetchScheduleForExport = async () => {
    try {
      const res = await axios.get(`${API}/event-settings/schedule`, { headers: getAuthHeader() });
      const items = (res.data || []).slice().sort((a, b) => b.sort_date.localeCompare(a.sort_date));
      setExportEventList(items.map((e) => e.event_date).filter(Boolean));
      // Build the display→sort_date lookup so Archive & Clear can
      // short-circuit the "is this a past event?" check locally.
      const dateMap = {};
      items.forEach((e) => { if (e.event_date) dateMap[e.event_date] = e.sort_date; });
      setEventSortDates(dateMap);
    } catch {
      // Non-fatal — dropdown just won't show event-specific options.
    }
  };

  useEffect(() => {
    filterRequests();
  }, [requests, selectedStatus, searchQuery]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/repair-requests/stats/dashboard`, {
        headers: getAuthHeader()
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/repair-requests`, {
        headers: getAuthHeader()
      });
      setRequests(response.data);
    } catch (error) {
      console.error('Error fetching requests:', error);
      toast.error('Failed to load repair requests');
    } finally {
      setLoading(false);
    }
  };

  const filterRequests = () => {
    let filtered = [...requests];

    if (selectedStatus) {
      if (selectedStatus === 'No Show') {
        // Synthetic filter — pull from Cancelled rows whose reason is no-show
        filtered = filtered.filter((req) => isNoShow(req));
      } else if (selectedStatus === 'Cancelled') {
        // Real "Cancelled" excludes the no-show subset so the two filters
        // never double-count each other in the table.
        filtered = filtered.filter((req) => req.status === 'Cancelled' && !isNoShow(req));
      } else {
        filtered = filtered.filter((req) => req.status === selectedStatus);
      }
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(req =>
        req.customer_name.toLowerCase().includes(query) ||
        req.customer_email.toLowerCase().includes(query) ||
        req.item_name.toLowerCase().includes(query)
      );
    }

    setFilteredRequests(filtered);
  };

  const handleViewRequest = (request) => {
    setSelectedRequest(request);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedRequest(null);
    fetchRequests(); // Refresh list after changes
  };

  const getStatusCount = (status) => {
    if (status === 'No Show') {
      return requests.filter((req) => isNoShow(req)).length;
    }
    if (status === 'Cancelled') {
      // Exclude no-show subset so the two pills tally cleanly.
      return requests.filter((req) => req.status === 'Cancelled' && !isNoShow(req)).length;
    }
    return requests.filter((req) => req.status === status).length;
  };

  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully');
  };

  // Trigger a browser download of the .xlsx repair-requests log. We use a
  // fetch + blob flow (rather than just window.open) so the auth header
  // can be attached — the backend gates this on admin/super_admin.
  // Optional `eventLabel` (event_schedule display string, e.g. "July 4th,
  // 2026") restricts the export to that single event.
  const handleExportLog = async (eventLabel = '') => {
    try {
      const params = eventLabel ? { event: eventLabel } : {};
      const resp = await axios.get(`${API}/admin/export-repair-requests`, {
        headers: getAuthHeader(),
        params,
        responseType: 'blob',
      });
      const blob = new Blob([resp.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const today = new Date().toISOString().slice(0, 10);
      const safeLabel = eventLabel
        ? eventLabel.replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '').slice(0, 60)
        : '';
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = safeLabel
        ? `repair-requests-${safeLabel}-${today}.xlsx`
        : `repair-requests-export-${today}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(a.href);
      toast.success(eventLabel ? `Exported ${eventLabel}` : 'Log exported');
      // Mark this event as "exported during this session" so the
      // Archive & Clear button can unlock for it. We don't persist
      // across reloads on purpose — re-export protects against an
      // admin returning hours later and accidentally clearing data
      // they didn't actually re-save.
      if (eventLabel) {
        setExportedThisSession((prev) => {
          const next = new Set(prev);
          next.add(eventLabel);
          return next;
        });
      }
    } catch (e) {
      toast.error(e.response?.status === 403
        ? 'You do not have permission to export logs'
        : 'Failed to export log');
    }
  };

  // ─── Archive & clear ──────────────────────────────────────────────
  // Computed gating: button is enabled only when:
  //   1. A specific event (not "All events") is selected
  //   2. That event's sort_date is strictly in the past
  //   3. The admin has exported THAT event during this session
  const todayIso = new Date().toISOString().slice(0, 10);
  const archiveEventSortDate = exportEvent ? eventSortDates[exportEvent] : null;
  const archiveIsPast = !!archiveEventSortDate && archiveEventSortDate < todayIso;
  const archiveExported = exportEvent && exportedThisSession.has(exportEvent);
  const archiveEnabled = !!exportEvent && archiveIsPast && archiveExported && !archiving;

  const handleArchiveAndClear = async () => {
    if (!archiveEnabled) return;
    const ok = window.confirm(
      'This will permanently remove all repair requests for the selected ' +
        'event after confirming they have been exported. This action ' +
        'cannot be undone.',
    );
    if (!ok) return;
    setArchiving(true);
    try {
      const resp = await axios.delete(
        `${API}/admin/repair-requests/by-event`,
        { headers: getAuthHeader(), params: { event: exportEvent } },
      );
      const n = resp.data?.deleted ?? 0;
      toast.success(
        `Repair requests for this event have been archived and cleared. (${n} removed.)`,
      );
      // Refresh the table so the cleared rows disappear immediately.
      await fetchRequests();
      await fetchStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to archive & clear');
    } finally {
      setArchiving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b-2 border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
            <div className="flex items-center space-x-3 min-w-0">
              <img 
                src="https://customer-assets.emergentagent.com/job_waste-less-hub-1/artifacts/918kjlll_image.png"
                alt="Repair Cafe Logo"
                className="w-10 h-10 sm:w-12 sm:h-12 rounded-full flex-shrink-0"
              />
              <div className="min-w-0">
                <h1 className="text-lg sm:text-2xl font-bold text-[#193460] truncate">
                  {user?.role === 'super_admin' ? 'Super Admin Dashboard' : 
                   user?.role === 'admin' ? 'Admin Dashboard' : 'Volunteer Dashboard'}
                </h1>
                <p className="text-xs sm:text-sm text-gray-600 truncate">
                  {user?.role === 'super_admin' ? 'System management & oversight' :
                   user?.role === 'admin' ? 'Manage repair requests' : 'Your assigned repairs'}
                </p>
              </div>
            </div>
            <div className="flex items-center justify-between sm:justify-end gap-3 flex-wrap">
              {user?.role === 'volunteer' && (
                <Button
                  onClick={() => toast.info('Please contact your administrator for assistance')}
                  variant="outline"
                  className="border-[#E87B6A] text-[#E87B6A] hover:bg-[#E87B6A] hover:text-white text-xs sm:text-sm"
                >
                  Help / Contact Admin
                </Button>
              )}
              <div className="text-right min-w-0">
                <p className="text-xs sm:text-sm font-semibold text-[#193460] truncate">{user?.name}</p>
                <p className="text-[10px] sm:text-xs text-gray-600 truncate max-w-[180px]">{user?.email}</p>
                <p className="text-[10px] sm:text-xs text-gray-500 capitalize">({user?.role})</p>
              </div>
              <Button
                variant="outline"
                onClick={handleLogout}
                className="border-2 border-[#193460] text-[#193460] hover:bg-[#193460] hover:text-white"
              >
                <LogOut className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Logout</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation Tabs - Role-based */}
      {(user?.role === 'admin' || user?.role === 'super_admin') && (
        <div className="bg-white border-b-2 border-gray-200" data-testid="admin-tabs">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Mobile: compact dropdown selector. Desktop: horizontal tabs. */}
            <MobileTabSelect
              currentView={view}
              onChange={setView}
              userRole={user?.role}
            />
            <div className="hidden md:flex md:space-x-1">
              <DesktopTabs
                currentView={view}
                onChange={setView}
                userRole={user?.role}
              />
            </div>
          </div>
        </div>
      )}

      {/* Content Area */}
      {view === 'users' && (user?.role === 'admin' || user?.role === 'super_admin') ? (
        <UserManagement />
      ) : view === 'categories' && user?.role === 'super_admin' ? (
        <CategoryManagement />
      ) : view === 'event-settings' && user?.role === 'super_admin' ? (
        <EventSettings />
      ) : view === 'schedule' && (user?.role === 'admin' || user?.role === 'super_admin') ? (
        <ScheduleManager />
      ) : view === 'slideshow' && user?.role === 'super_admin' ? (
        <SlideshowManager />
      ) : view === 'email-templates' && user?.role === 'super_admin' ? (
        <EmailTemplatesManager />
      ) : (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Role-Specific Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {user?.role === 'volunteer' ? (
              <>
                <Card className="border-2 border-purple-200 bg-purple-50">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm text-gray-600">My Assigned Repairs</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-purple-600">{stats.my_assigned || 0}</div>
                  </CardContent>
                </Card>
                <Card className="border-2 border-green-200 bg-green-50">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm text-gray-600">My Completed Repairs</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-green-600">{stats.my_completed || 0}</div>
                  </CardContent>
                </Card>
              </>
            ) : (
              <>
                <Card className="border-2 border-blue-200 bg-blue-50">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm text-gray-600">Total Requests</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-blue-600">{stats.total_requests || 0}</div>
                  </CardContent>
                </Card>
                <Card className="border-2 border-orange-200 bg-orange-50">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm text-gray-600">Unassigned</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-orange-600">{stats.unassigned || 0}</div>
                  </CardContent>
                </Card>
                <Card className="border-2 border-purple-200 bg-purple-50">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm text-gray-600">Assigned</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-purple-600">{stats.assigned || 0}</div>
                  </CardContent>
                </Card>
                <Card className="border-2 border-green-200 bg-green-50">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm text-gray-600">Completed</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-green-600">{stats.by_status?.Completed || 0}</div>
                  </CardContent>
                </Card>
              </>
            )}
          </div>
        )}
        
        {/* Trends Chart for Super Admin */}
        {user?.role === 'super_admin' && stats && stats.trends && stats.trends.length > 0 && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="text-lg flex items-center">
                <TrendingUp className="w-5 h-5 mr-2 text-blue-600" />
                Request Trends (Last 7 Days)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={stats.trends}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="date" 
                    tickFormatter={(value) => {
                      const date = new Date(value);
                      return `${date.getMonth() + 1}/${date.getDate()}`;
                    }}
                  />
                  <YAxis />
                  <Tooltip 
                    labelFormatter={(value) => {
                      const date = new Date(value);
                      return date.toLocaleDateString();
                    }}
                    formatter={(value) => [`${value} requests`, 'Count']}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="count" 
                    stroke="#E87B6A" 
                    strokeWidth={2}
                    dot={{ fill: '#E87B6A', r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}
        
        {/* Quick Assign Section for Admin/Super Admin */}
        {(user?.role === 'admin' || user?.role === 'super_admin') && stats && stats.unassigned > 0 && (
          <Card className="mb-6 border-2 border-orange-300 bg-orange-50">
            <CardHeader>
              <CardTitle className="text-lg text-orange-700 flex items-center">
                <Settings className="w-5 h-5 mr-2" />
                Quick Assign ({stats.unassigned} Unassigned Requests)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-700 mb-3">
                Click on any unassigned request below to quickly assign it to a volunteer
              </p>
            </CardContent>
          </Card>
        )}

        {/* Stats Cards by Status */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          {statuses.map(status => (
            <Card 
              key={status}
              className={`cursor-pointer transition-all duration-300 ${
                selectedStatus === status ? 'ring-2 ring-[#E87B6A] shadow-lg' : 'hover:shadow-md'
              }`}
              onClick={() => setSelectedStatus(selectedStatus === status ? '' : status)}
            >
              <CardContent className="p-4 text-center">
                <div className="text-2xl font-bold text-[#193460] mb-1">
                  {getStatusCount(status)}
                </div>
                <div className="text-xs text-gray-600">{status}</div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Search and Filter */}
        <Card className="mb-6 border-2 border-gray-200">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <Input
                  type="text"
                  placeholder="Search by name, email, or item..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 h-12 border-2 border-gray-300 focus:border-[#E87B6A]"
                />
              </div>
              <Button
                variant="outline"
                onClick={() => {
                  setSelectedStatus('');
                  setSearchQuery('');
                }}
                className="border-2 border-gray-300 hover:bg-gray-100"
              >
                <Filter className="w-4 h-4 mr-2" />
                Clear Filters
              </Button>
            </div>
            {(selectedStatus || searchQuery) && (
              <div className="mt-4 flex items-center gap-2">
                <span className="text-sm text-gray-600">Active filters:</span>
                {selectedStatus && (
                  <Badge className={STATUS_COLORS[selectedStatus]}>
                    Status: {selectedStatus}
                  </Badge>
                )}
                {searchQuery && (
                  <Badge className="bg-gray-100 text-gray-800">
                    Search: "{searchQuery}"
                  </Badge>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Requests Table */}
        <Card className="border-2 border-gray-200">
          <CardHeader>
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
              <CardTitle className="text-2xl text-[#193460]">
                Repair Requests ({filteredRequests.length})
              </CardTitle>
              <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                <label htmlFor="export-event-filter" className="sr-only">
                  Filter export by event
                </label>
                <select
                  id="export-event-filter"
                  data-testid="export-event-filter"
                  value={exportEvent}
                  onChange={(e) => setExportEvent(e.target.value)}
                  className="rounded-md border-2 border-gray-300 bg-white py-2 px-3 text-sm text-[#193460] focus:outline-none focus:ring-2 focus:ring-[#193460] sm:min-w-[200px]"
                >
                  <option value="">All events</option>
                  {exportEventList.map((label) => (
                    <option key={label} value={label}>{label}</option>
                  ))}
                </select>
                <Button
                  onClick={() => handleExportLog(exportEvent)}
                  data-testid="export-log-btn"
                  size="sm"
                  className="bg-[#193460] hover:bg-[#0f2240] text-white"
                >
                  <Download className="w-4 h-4 mr-2" />
                  {exportEvent ? 'Export Selected Event' : 'Export Log (.xlsx)'}
                </Button>
                {/* Archive & Clear — destructive, deliberately styled
                    differently. Disabled until the operator has both
                    (a) selected a past event and (b) exported it in
                    the current session. */}
                <Button
                  onClick={handleArchiveAndClear}
                  disabled={!archiveEnabled}
                  data-testid="archive-clear-btn"
                  size="sm"
                  variant="outline"
                  className="border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  title={
                    !exportEvent
                      ? 'Pick a specific event from the dropdown first'
                      : !archiveIsPast
                      ? 'Only past events can be archived'
                      : !archiveExported
                      ? 'Export this event first — Archive & Clear unlocks after a successful export'
                      : 'Permanently remove all repair requests for this event'
                  }
                >
                  {archiving ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4 mr-2" />
                  )}
                  Archive &amp; Clear Event Requests
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-[#E87B6A]" />
                <span className="ml-3 text-gray-600">Loading requests...</span>
              </div>
            ) : filteredRequests.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 text-lg">No repair requests found</p>
                <p className="text-gray-500 text-sm mt-2">
                  {selectedStatus || searchQuery ? 'Try adjusting your filters' : 'Requests will appear here'}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full md:min-w-[720px]" data-testid="repair-requests-table">
                  <thead className="bg-gray-50 border-b-2 border-gray-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-[#193460]">Customer</th>
                      <th className="hidden md:table-cell px-4 py-3 text-left text-sm font-semibold text-[#193460]">Item</th>
                      <th className="hidden md:table-cell px-4 py-3 text-left text-sm font-semibold text-[#193460]">Status</th>
                      <th className="hidden md:table-cell px-4 py-3 text-left text-sm font-semibold text-[#193460]">Phone Number</th>
                      <th className="hidden md:table-cell px-4 py-3 text-left text-sm font-semibold text-[#193460]">Date</th>
                      <th className="px-4 py-3 text-right md:text-left text-sm font-semibold text-[#193460]">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRequests.map((request, index) => (
                      <tr 
                        key={request.id}
                        className={`border-b border-gray-200 hover:bg-gray-50 transition-colors ${
                          index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'
                        }`}
                      >
                        <td className="px-4 py-4">
                          <div className="flex items-start space-x-3">
                            <div className="w-10 h-10 bg-[#E87B6A] rounded-full flex items-center justify-center flex-shrink-0">
                              <User className="w-5 h-5 text-white" />
                            </div>
                            <div>
                              <p className="font-semibold text-[#193460]" data-testid={`customer-name-${request.id}`}>{request.customer_name}</p>
                              <p className="hidden md:flex text-xs text-gray-600 items-center mt-1">
                                <Mail className="w-3 h-3 mr-1" />
                                {request.customer_email}
                              </p>
                              {/* Phone number now lives in its own dedicated column
                                  (see the Phone Number cell below) — no longer
                                  duplicated here to keep the Customer cell tidy. */}
                            </div>
                          </div>
                        </td>
                        <td className="hidden md:table-cell px-4 py-4">
                          <p className="font-medium text-[#193460]">{request.item_name}</p>
                          <p className="text-sm text-gray-600 mt-1 line-clamp-2">{request.item_description}</p>
                        </td>
                        <td className="hidden md:table-cell px-4 py-4">
                          {(() => {
                            const display = getDisplayStatus(request);
                            return (
                              <Badge className={`${STATUS_COLORS[display] || STATUS_COLORS[request.status]} border whitespace-normal text-left`} data-testid={`status-badge-${request.id}`}>
                                {request.status === 'Assigned' && (request.assigned_to_name || request.assigned_to)
                                  ? `Assigned to ${request.assigned_to_name || request.assigned_to}`
                                  : display}
                              </Badge>
                            );
                          })()}
                        </td>
                        <td className="hidden md:table-cell px-4 py-4 align-top">
                          {/* Phone Number column — replaces the old "Assigned" column
                              (assignment info is still shown in the Status badge and
                              in the request detail view). `break-all` prevents an
                              unusually long or unformatted number from expanding
                              the column and breaking the responsive layout. */}
                          {request.customer_phone && request.customer_phone.trim() ? (
                            <a
                              href={`tel:${request.customer_phone.replace(/\s+/g, '')}`}
                              className="text-sm text-gray-700 hover:text-[#193460] hover:underline break-all"
                              data-testid={`phone-cell-${request.id}`}
                            >
                              {request.customer_phone}
                            </a>
                          ) : (
                            <span
                              className="text-sm text-gray-400 italic"
                              data-testid={`phone-cell-${request.id}`}
                            >
                              Not provided
                            </span>
                          )}
                        </td>
                        <td className="hidden md:table-cell px-4 py-4">
                          <div className="flex items-center text-sm text-gray-600">
                            <Calendar className="w-4 h-4 mr-1" />
                            {new Date(request.created_at).toLocaleDateString()}
                          </div>
                          {request.preferred_event && (
                            <div className="flex items-center text-xs text-gray-500 mt-1">
                              <MapPin className="w-3 h-3 mr-1" />
                              {request.preferred_event}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-4 text-right md:text-left">
                          <Button
                            size="sm"
                            onClick={() => handleViewRequest(request)}
                            className="bg-[#E87B6A] hover:bg-[#d66a59] text-white"
                            data-testid={`view-request-${request.id}`}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            View
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      )}

      {/* Request Detail Modal */}
      {showModal && selectedRequest && (
        <RequestDetailModal
          request={selectedRequest}
          onClose={handleCloseModal}
        />
      )}
    </div>
  );
}
